import {
  BoardIcon,
  CalendarDashboardIcon,
  ChatIcon,
  CloseIcon,
  DashboardIcon,
  DocumentIcon,
  EmbedViewIcon,
  GanttDashboardIcon,
  ListIcon,
  LockedIcon,
  PieChartIcon,
  TimeHourglassIcon,
  WhiteBoardIcon,
  WorksheetIcon,
} from '@sketchnote/icons';
import { Button } from '@sketchnote/shared-components';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useFileExploreStore } from 'src/components/file-explore';
import { PLANRULES } from 'src/constants/plans';
import { useBoardStore } from 'src/features/boards';
import { useAuth } from 'src/shared/hooks/use-auth';

import { fileViewList } from '../assets/data';
import { useFileStore } from '../store/useFileStore';
const { useState, useCallback } = React;

const ALLOWED_MULTIPLE_VIEW_TYPES = ['embed', 'document', 'whiteboard', 'list'];

export const SelectFileView = ({ onClose }: { onClose: () => void }) => {
  const [activeMenu, setActiveMenu] = useState('document');
  const isActive = (item: string) => item === activeMenu;
  const authData = useAuth((s) => s.userDetails);
  const handleMenuClick = (value: string) => {
    setActiveMenu(value);
  };

  const router = useRouter();
  const isAgile = router?.query?.type === 'agile';

  const selectViewMenuItems = [
    !isAgile && {
      sectionTitle: 'Task views',
      items: [
        {
          icon: <ListIcon />,
          label: 'List',
          value: 'list',
          restricted: authData?.plan.type ? !PLANRULES.listcreation[authData?.plan.type] : false,
        },
        {
          icon: <BoardIcon />,
          label: 'Kanban',
          value: 'kanban',
        },
        {
          icon: <GanttDashboardIcon className="mt-2" />,
          label: 'Gantt',
          value: 'gantt',
          restricted: authData?.plan.type ? !PLANRULES.ganttcreation[authData?.plan.type] : false,
        },
        {
          icon: <CalendarDashboardIcon className="mt-2" />,
          label: 'Calendar',
          value: 'calendar',
          restricted: authData?.plan.type
            ? !PLANRULES.calendarcreation[authData?.plan.type]
            : false,
        },
        {
          icon: <TimeHourglassIcon className="mt-2" />,
          label: 'Timesheet',
          value: 'timesheet',
          restricted: authData?.plan.type
            ? !PLANRULES.timesheetcreation[authData?.plan.type]
            : false,
        },
        {
          icon: <PieChartIcon className="mt-2" />,
          label: 'Analytics',
          value: 'analytics',
          restricted: authData?.plan.type
            ? !PLANRULES.analyticscreation[authData?.plan.type]
            : false,
        },
      ],
    },
    {
      sectionTitle: 'Page views',
      items: [
        {
          icon: <WhiteBoardIcon />,
          label: 'Whiteboard',
          value: 'whiteboard',
          restricted: authData?.plan.type
            ? !PLANRULES.whiteboardcreation[authData?.plan.type]
            : false,
        },
        {
          icon: <DocumentIcon />,
          label: 'Document',
          value: 'document',
        },
      ],
    },
    {
      separator: true,
      items: [
        {
          icon: <EmbedViewIcon className="mt-3" size="1.1em" />,
          label: 'Embed',
          value: 'embed',
          restricted: authData?.plan.type ? !PLANRULES.embedcreation[authData?.plan.type] : false,
        },
        {
          icon: <WorksheetIcon />,
          label: 'Worksheet',
          value: 'sheet',
        },
        {
          icon: <ChatIcon />,
          label: 'Chat',
          value: 'chat',
          restricted: authData?.plan.type ? !PLANRULES.chat[authData?.plan.type] : false,
        },
      ],
    },
  ].filter(Boolean);

  const isFileTypeRestricted = (fileType: string) => {
    switch (fileType) {
      case 'timesheet':
        return authData?.plan.type ? !PLANRULES.timesheetcreation[authData?.plan.type] : false;
      case 'list':
        return authData?.plan.type ? !PLANRULES.listcreation[authData?.plan.type] : false;
      case 'whiteboard':
        return authData?.plan.type ? !PLANRULES.whiteboardcreation[authData?.plan.type] : false;
      case 'gantt':
        return authData?.plan.type ? !PLANRULES.ganttcreation[authData?.plan.type] : false;
      case 'form':
        return authData?.plan.type ? !PLANRULES.formcreation[authData?.plan.type] : false;
      case 'analytics':
        return authData?.plan.type ? !PLANRULES.analyticscreation[authData?.plan.type] : false;
      case 'embed':
        return authData?.plan.type ? !PLANRULES.embedcreation[authData?.plan.type] : false;
      case 'chat':
        return authData?.plan.type ? !PLANRULES.chat[authData?.plan.type] : false;
      case 'calendar':
        return authData?.plan.type ? !PLANRULES.calendarcreation[authData?.plan.type] : false;
      default:
        false;
    }
  };

  return (
    <div className="flex rounded-lg overflow-auto">
      <div className="bg-gray-199 px-20 py-24" style={{ width: '210px' }}>
        <nav className="flex flex-col text-gray-600">
          {selectViewMenuItems?.map((viewMenuItemsList, index) => {
            if (!viewMenuItemsList) return null;
            const { sectionTitle, items, separator = false } = viewMenuItemsList;

            return (
              <React.Fragment key={`${sectionTitle}-${index}`}>
                {sectionTitle && (
                  <span className="text-xs mb-10 mt-16 font-medium text-gray-500">
                    {sectionTitle}
                  </span>
                )}
                {separator && <hr className="mt-16 pt-4" />}
                {items?.map(({ label, value, icon, restricted = false }, index) => (
                  <div
                    key={`${value}-${index}`}
                    className={classNames({
                      'text-sm flex justify-between text-gray-600 py-8 px-8 cursor-pointer': true,
                      'text-primary-500 bg-primary-100 hover:bg-blue-100': isActive(value),
                    })}
                    onClick={handleMenuClick?.bind(this, value)}
                  >
                    <div className="flex">
                      <span className="mt-2 block">{icon}</span>
                      <span className="px-8 font-medium">{label}</span>
                    </div>
                    {restricted && (
                      <LockedIcon className="ml-8 mt-3 text-gray-500 inline-block text-sm" />
                    )}
                  </div>
                ))}
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      <div className="bg-white mt-20">
        <div className="w-full flex justify-end px-24">
          <button
            className="text-xs text-gray-600 align-self-end items-end justify-items-end"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div style={{ width: '390px' }}>
          <FileView
            imageURL={fileViewList[activeMenu].imageURL}
            title={fileViewList[activeMenu].title}
            description={fileViewList[activeMenu].description}
            value={fileViewList[activeMenu].value}
            inUse={isInternalFileInUse(fileViewList[activeMenu].value)}
            onClose={onClose}
            isRestricted={isFileTypeRestricted(fileViewList[activeMenu].value)}
          />
        </div>
      </div>
    </div>
  );
};

export const isInternalFileInUse = (internalFileType: string) => {
  const router = useRouter();
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  if (ALLOWED_MULTIPLE_VIEW_TYPES.includes(internalFileType)) return false;

  return (
    currentFile?.internalFiles?.some(
      ({ type, view }) => type === internalFileType || view === internalFileType
    ) || false
  );
};

const FileView = ({
  imageURL,
  title,
  description,
  inUse = true,
  value,
  onClose,
  isRestricted,
}: {
  imageURL: string;
  title: string;
  description: string;
  inUse: boolean;
  value: string;
  onClose: () => void;
  isRestricted?: boolean;
}) => {
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const boardsStore = useBoardStore();
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  const { closeExploreMenu } = useFileExploreStore();

  const onAddView = useFileStore((s) => s.onAddView);

  const handleAddview = async () => {
    const response: any = await onAddView({
      fileId: currentFile?.id || '',
      type: value,
    });

    onClose();
    //Check status
    if (response && response.id) {
      closeExploreMenu();
      if (response.type === 'board') {
        boardsStore.actions.fetchBoard(response.id);
      }
      router.push({
        pathname: `/${mode}/${orgId}/files/${router.query.slug}`,
        query: {
          view: response.id,
          type: value,
          viewId: response.viewId,
        },
      });
    }
  };
  const inUseView =
    inUse ||
    (currentFile?.primaryFile?.type === value &&
      !ALLOWED_MULTIPLE_VIEW_TYPES.includes(currentFile?.primaryFile?.type));
  //   ||
  // currentFile?.primaryFile?.view === value;
  return (
    <div className="flex flex-col">
      {/* to be redone with h-auto */}
      <div style={{ height: '424px' }} className="p-24 ">
        <div className="flex mb-8">
          <p className="text-gray-999 font-medium">{title}</p>
          {inUseView && (
            <span className="ml-10 px-8 py-1 bg-green-800 text-white text-sm rounded-sm">
              In use
            </span>
          )}

          {title === 'Document' && (
            <span className="bg-yellow-701 ml-12 rounded-sm px-4 py-2 text-blue-700 text-sm font-semibold">
              Beta
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">{description}</p>

        <div className="mt-24">
          <img src={imageURL} alt="file-view-type" className="w-342 h-192" />
        </div>
        {isRestricted && (
          <div className="w-full bg-red-500 text-white text-center mt-12 rounded-sm text-xs p-6">
            <span>This feature is locked in your current plan.</span>
          </div>
        )}
      </div>
      <div className="w-full flex justify-end mt-64 py-12 border-t px-24 border-gray-200">
        <Button
          status="primary"
          className="ml-10"
          disabled={inUseView || isRestricted}
          onClick={inUseView || isRestricted ? undefined : handleAddview}
        >
          Add view
        </Button>
      </div>
    </div>
  );
};
